import React, { useState, useEffect } from 'react';
import {
  X,
  ClipboardList,
  CheckCircle2,
  UtensilsCrossed,
  Package,
  Bike,
  MapPin,
  Clock,
  Phone,
  IndianRupee,
  AlertCircle,
} from 'lucide-react';

const OrderTrackingModal = ({ order, onClose, isDark }) => {
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const steps = [
    { id: 1, name: 'Order Placed', key: 'created_at', time: order?.created_at, icon: ClipboardList },
    { id: 2, name: 'Restaurant Accepted', key: 'restro_accept_time', time: order?.restro_accept_time, icon: UtensilsCrossed },
    { id: 3, name: 'Preparing Food', key: 'restro_accept_time', time: order?.restro_accept_time, icon: Package },
    { id: 4, name: 'Ready for Pickup', key: 'restro_ready_time', time: order?.restro_ready_time, icon: CheckCircle2 },
    { id: 5, name: 'Rider Pickup', key: 'rider_pickup_time', time: order?.rider_pickup_time, icon: Bike },
    { id: 6, name: 'Order Delivered', key: 'order_delivered_time', time: order?.order_delivered_time, icon: MapPin },
  ];

  useEffect(() => {
    let stepIndex = 0;
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].time && steps[i].time !== 'null' && steps[i].time !== null) {
        stepIndex = i;
        break;
      }
    }
    setCurrentStepIndex(stepIndex);
  }, [order]);

  useEffect(() => {
    if (order?.restro_accept_time) {
      const acceptedTime = new Date(order.restro_accept_time);
      const estimatedMinutes = order.order_status_driver === 'delivered'
        ? 0
        : order.rider_pickup_time
        ? 15
        : order.restro_ready_time
        ? 25
        : 35;
      const estimated = new Date(acceptedTime.getTime() + estimatedMinutes * 60000);
      setEstimatedTime(estimated);
    }
  }, [order]);

  const progressPercent = ((currentStepIndex + 1) / steps.length) * 100;
  const isDelivered = order?.order_status_driver === 'delivered';
  const isCancelled = order?.order_status_restro === 'cancelled';

  const getVehicleStatus = () => {
    if (isDelivered) return 'Order Delivered Successfully!';
    if (isCancelled) return 'Order Cancelled';
    if (order?.rider_pickup_time) return 'Rider on the way';
    if (order?.restro_ready_time) return 'Waiting for rider pickup';
    if (order?.restro_accept_time) return 'Preparing your order';
    return 'Order being processed';
  };

  const colors = {
    primary: '#3b82f6',
    success: '#22c55e',
    danger: '#ef4444',
    warning: '#f59e0b',
    dark: {
      bg: '#0f1420',
      surface: '#1a1f35',
      border: '#2a3150',
      text: '#f1f5f9',
      textSub: '#94a3b8',
    },
    light: {
      bg: '#ffffff',
      surface: '#f8fafc',
      border: '#e2e8f0',
      text: '#0f172a',
      textSub: '#64748b',
    },
  };

  const theme = isDark ? colors.dark : colors.light;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 500,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.bg,
          borderRadius: '24px',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          border: `1px solid ${theme.border}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: isDark
            ? '0 20px 60px rgba(0,0,0,0.4)'
            : '0 20px 60px rgba(0,0,0,0.1)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: `1px solid ${theme.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: isDark ? '#1a1f35' : '#f8fafc',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: theme.text }}>
              📍 Track Your Order
            </h2>
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: theme.textSub }}>
              Order #{order?.id} • {order?.receiver_name || `User ${order?.userid}`}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.textSub,
              padding: '8px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => (e.target.style.background = isDark ? '#2a3150' : '#e2e8f0')}
            onMouseLeave={(e) => (e.target.style.background = 'none')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '24px',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {/* Progress Bar */}
          <div style={{ marginBottom: '32px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '13px',
                fontWeight: 600,
                color: theme.text,
              }}
            >
              <span>Order Progress</span>
              <span style={{ color: colors.primary }}>{Math.round(progressPercent)}%</span>
            </div>
            <div
              style={{
                background: theme.surface,
                borderRadius: '12px',
                height: '8px',
                overflow: 'hidden',
                border: `1px solid ${theme.border}`,
              }}
            >
              <div
                style={{
                  width: `${progressPercent}%`,
                  background: `linear-gradient(90deg, ${colors.primary}, #8b5cf6)`,
                  height: '100%',
                  borderRadius: '12px',
                  transition: 'width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              />
            </div>
          </div>

          {/* Order Info Cards - Horizontal */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '28px',
            }}
          >
            {/* Restaurant Card */}
            <div
              style={{
                background: theme.surface,
                padding: '16px',
                borderRadius: '14px',
                border: `1px solid ${theme.border}`,
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.primary;
                e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '8px',
                }}
              >
                <UtensilsCrossed size={18} color={colors.primary} />
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: theme.textSub,
                    textTransform: 'uppercase',
                  }}
                >
                  Restaurant
                </span>
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: theme.text }}>
                {order?.designation || 'N/A'}
              </div>
              <div style={{ fontSize: '11px', color: theme.textSub, marginTop: '6px' }}>
                ID: {order?.designation_id}
              </div>
            </div>

            {/* Customer Card */}
            <div
              style={{
                background: theme.surface,
                padding: '16px',
                borderRadius: '14px',
                border: `1px solid ${theme.border}`,
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.primary;
                e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '8px',
                }}
              >
                <Phone size={18} color={colors.primary} />
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: theme.textSub,
                    textTransform: 'uppercase',
                  }}
                >
                  Customer
                </span>
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: theme.text }}>
                {order?.receiver_name || 'N/A'}
              </div>
              <div style={{ fontSize: '11px', color: theme.textSub, marginTop: '6px' }}>
                📞 {order?.receiver_phone || 'N/A'}
              </div>
            </div>

            {/* Payment Card */}
            <div
              style={{
                background: theme.surface,
                padding: '16px',
                borderRadius: '14px',
                border: `1px solid ${theme.border}`,
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.success;
                e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.success}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '8px',
                }}
              >
                <IndianRupee size={18} color={colors.success} />
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: theme.textSub,
                    textTransform: 'uppercase',
                  }}
                >
                  Payment
                </span>
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: theme.text }}>
                ₹{order?.final_amount || order?.price || 0}
              </div>
              <div style={{ fontSize: '11px', color: theme.textSub, marginTop: '6px' }}>
                {order?.pay_method || 'COD'} • <span style={{ color: colors.success }}>Pending</span>
              </div>
            </div>
          </div>

          {/* Status Section */}
          {!isDelivered && !isCancelled && (
            <div
              style={{
                background: `${colors.primary}15`,
                border: `2px solid ${colors.primary}40`,
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '28px',
                animation: 'pulse-border 2s infinite',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    background: colors.primary,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'bikeMove 1.5s ease-in-out infinite',
                  }}
                >
                  <Bike size={32} color='white' strokeWidth={1.5} />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: theme.text,
                    }}
                  >
                    {getVehicleStatus()}
                  </div>
                  <div
                    style={{
                      fontSize: '13px',
                      color: theme.textSub,
                      marginTop: '4px',
                    }}
                  >
                    {order?.rider_pickup_time
                      ? 'Rider is on the move'
                      : order?.restro_ready_time
                      ? 'Waiting for rider pickup'
                      : 'Order being prepared'}
                  </div>
                  {order?.delivery_person_id && (
                    <div
                      style={{
                        fontSize: '11px',
                        color: theme.textSub,
                        marginTop: '6px',
                      }}
                    >
                      🆔 Rider ID: {order.delivery_person_id}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {isCancelled && (
            <div
              style={{
                background: `${colors.danger}15`,
                border: `2px solid ${colors.danger}40`,
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '28px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  fontSize: '16px',
                  fontWeight: 700,
                  color: colors.danger,
                }}
              >
                <AlertCircle size={24} />
                Order Cancelled
              </div>
              {order?.user_cancel_remarks && (
                <div
                  style={{
                    fontSize: '12px',
                    color: theme.textSub,
                    marginTop: '8px',
                  }}
                >
                  Reason: {order.user_cancel_remarks}
                </div>
              )}
            </div>
          )}

          {/* Timeline - Horizontal Professional */}
          <div
            style={{
              marginBottom: '24px',
              overflowX: 'auto',
              paddingBottom: '12px',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '0',
                minWidth: '100%',
                position: 'relative',
              }}
            >
              {steps.map((step, idx) => {
                const isCompleted = step.time && step.time !== 'null' && step.time !== null;
                const isActive = idx === currentStepIndex && !isCompleted && !isDelivered;
                const isCurrent = idx === currentStepIndex;
                const IconComponent = step.icon;

                return (
                  <div
                    key={step.id}
                    style={{
                      flex: 1,
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    {/* Connecting Line */}
                    {idx < steps.length - 1 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '28px',
                          left: '50%',
                          width: '100%',
                          height: '3px',
                          background: isCompleted
                            ? colors.success
                            : theme.border,
                          zIndex: 0,
                        }}
                      />
                    )}

                    {/* Icon Container */}
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '14px',
                        background: isCompleted
                          ? colors.success
                          : isActive
                          ? colors.primary
                          : theme.surface,
                        border: `2px solid ${
                          isCompleted
                            ? colors.success
                            : isActive
                            ? colors.primary
                            : isCurrent
                            ? colors.primary
                            : theme.border
                        }`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        position: 'relative',
                        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        boxShadow: isCurrent
                          ? `0 0 0 4px ${colors.primary}25`
                          : 'none',
                      }}
                    >
                      {isCompleted ? (
                        <CheckCircle2 size={28} color='white' strokeWidth={2.5} />
                      ) : (
                        <IconComponent
                          size={28}
                          color={
                            isActive
                              ? 'white'
                              : theme.text === '#f1f5f9'
                              ? '#94a3b8'
                              : '#64748b'
                          }
                          strokeWidth={1.5}
                        />
                      )}
                    </div>

                    {/* Label */}
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: isCurrent ? colors.primary : theme.text,
                        marginTop: '12px',
                        textAlign: 'center',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {step.name}
                    </div>

                    {/* Time */}
                    {step.time && step.time !== 'null' && (
                      <div
                        style={{
                          fontSize: '11px',
                          color: theme.textSub,
                          marginTop: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Clock size={12} />
                        {new Date(step.time).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ETA Card */}
          {!isDelivered && !isCancelled && (
            <div
              style={{
                background: isDark ? '#1a2844' : '#dbeafe',
                border: `2px solid ${colors.primary}50`,
                borderRadius: '16px',
                padding: '20px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: theme.textSub,
                  textTransform: 'uppercase',
                  marginBottom: '8px',
                }}
              >
                <Clock size={16} />
                Estimated Delivery
              </div>
              <div
                style={{
                  fontSize: '32px',
                  fontWeight: 700,
                  color: colors.primary,
                  marginBottom: '6px',
                }}
              >
                {estimatedTime
                  ? estimatedTime.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'Calculating...'}
              </div>
              <div style={{ fontSize: '12px', color: theme.textSub }}>
                {order?.rider_pickup_time
                  ? '🏍️ Rider on the way'
                  : order?.restro_ready_time
                  ? '⏳ Waiting for rider'
                  : '👨‍🍳 Being prepared'}
              </div>
            </div>
          )}

          {isDelivered && (
            <div
              style={{
                background: `${colors.success}15`,
                border: `2px solid ${colors.success}50`,
                borderRadius: '16px',
                padding: '20px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: colors.success,
                  marginBottom: '8px',
                }}
              >
                ✅ Order Delivered!
              </div>
              {order?.order_delivered_time && (
                <div style={{ fontSize: '12px', color: theme.textSub }}>
                  Delivered on {new Date(order.order_delivered_time).toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: `1px solid ${theme.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            background: isDark ? '#1a1f35' : '#f8fafc',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: `1px solid ${theme.border}`,
              background: 'transparent',
              color: theme.text,
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = theme.surface;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
            }}
          >
            Close
          </button>
        </div>

        {/* Animations */}
        <style>{`
          @keyframes bikeMove {
            0%, 100% { transform: translateX(0px) rotateZ(0deg); }
            50% { transform: translateX(8px) rotateZ(2deg); }
          }
          
          @keyframes pulse-border {
            0%, 100% { 
              border-color: ${colors.primary}40;
              box-shadow: 0 0 0 0 ${colors.primary}20;
            }
            50% { 
              border-color: ${colors.primary}60;
              box-shadow: 0 0 0 8px ${colors.primary}10;
            }
          }
          
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default OrderTrackingModal;
